import { useCallback, useSyncExternalStore } from 'react';

/**
 * Row density of every table (HR Module Prototype, Component Map › Row density): condensed 40px (the
 * default), regular 48px or relaxed 56px. Picked in a table's footer and remembered in this browser, so
 * all tables follow the same choice.
 */
export type TableDensity = 'condensed' | 'regular' | 'relaxed';
export const TABLE_DENSITIES: readonly TableDensity[] = ['condensed', 'regular', 'relaxed'];

const STORAGE_KEY = 'hr-table-density';
const listeners = new Set<() => void>();

const read = (): TableDensity => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return TABLE_DENSITIES.includes(stored as TableDensity)
      ? (stored as TableDensity)
      : 'condensed';
  } catch {
    return 'condensed';
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useTableDensity = () => {
  const density = useSyncExternalStore(subscribe, read, () => 'condensed' as TableDensity);
  const setDensity = useCallback((next: TableDensity) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage blocked (private window): the choice lasts until reload.
    }
    listeners.forEach((listener) => listener());
  }, []);
  return [density, setDensity] as const;
};

/** Each density's cell height and vertical padding. */
export const DENSITY_CELL_CLASS: Record<TableDensity, string> = {
  condensed: 'h-10 py-1',
  regular: 'h-12 py-2',
  relaxed: 'h-14 py-3',
};
